import { useConfirm as usePrimeConfirm } from 'primevue/useconfirm'
import type { AppUser } from '@/lib/types'
import { Roles } from '@/lib/types'

/**
 * UI metadata for a user — display label + role-tone + initials for
 * the avatar fallback. Rendered via PrimeVue `<Avatar>` and tag
 * components downstream; this helper centralizes the role→tone map
 * so styling stays consistent.
 */
export interface UserProfileDisplay {
  /** Display label — falls back through name → email → person_id → id. */
  label: string
  /** Two-character initials for `<Avatar>` text mode. */
  initials: string
  /** PrimeVue severity tone — used by `<Tag :severity="tone">`. */
  tone: 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast'
  /** Title-cased primary role label, for tooltip / badge text. */
  roleLabel: string
}

const ROLE_TONE: Record<string, UserProfileDisplay['tone']> = {
  [Roles.SUPER_ADMIN]: 'danger',
  [Roles.SYSTEM]: 'contrast',
  [Roles.TENANT_ADMIN]: 'success',
  [Roles.MANAGER]: 'info',
  [Roles.USER]: 'secondary',
  [Roles.GUEST]: 'secondary',
}

const ROLE_LABEL: Record<string, string> = {
  [Roles.SUPER_ADMIN]: 'Super Admin',
  [Roles.SYSTEM]: 'System',
  [Roles.TENANT_ADMIN]: 'Tenant Admin',
  [Roles.MANAGER]: 'Manager',
  [Roles.USER]: 'User',
  [Roles.GUEST]: 'Guest',
}

const initialsOf = (label: string): string => {
  const parts = label.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

/**
 * Derive presentation metadata for a user. Pure function — given
 * the same input always returns the same output. Safe to call in
 * computeds.
 */
export const getUserProfile = (user: AppUser): UserProfileDisplay => {
  const detail = user.detail
  const label = detail
    ? [detail.first_name, detail.last_name].filter(Boolean).join(' ') ||
      detail.email ||
      detail.person_id ||
      `User #${detail.id}`
    : 'Guest'

  // Primary role = first matching role from the canonical order.
  const orderedRoles = [
    Roles.SUPER_ADMIN,
    Roles.SYSTEM,
    Roles.TENANT_ADMIN,
    Roles.MANAGER,
    Roles.USER,
    Roles.GUEST,
  ] as string[]
  const primary = orderedRoles.find((r) => detail?.role_list?.includes(r)) ?? Roles.GUEST

  return {
    label,
    initials: initialsOf(label),
    tone: ROLE_TONE[primary] ?? 'secondary',
    roleLabel: ROLE_LABEL[primary] ?? primary,
  }
}

/**
 * Permission/role gate helpers. Use directly in `v-if` or computed
 * gates to skip rendering UI the actor can't operate.
 */
export const userHasRoles = (user: AppUser, roles: string[]): boolean => user.hasRoles(roles)

export const userHasPermission = (user: AppUser, permissions: string[]): boolean =>
  user.hasPermissions(permissions)

/**
 * Thin wrapper around PrimeVue's confirm dialog. Returns a Promise
 * so callers can `await` the user's decision instead of passing
 * accept/reject callbacks.
 *
 *   if (await useConfirm({ message: 'Delete this device?' })) {
 *     await deleteDevice()
 *   }
 *
 * Requires `<ConfirmDialog />` to be mounted in `App.vue` and the
 * `ConfirmationService` plugin to be registered in `main.ts`.
 */
export interface ConfirmDialogOptions {
  message: string
  header?: string
  icon?: string
  acceptLabel?: string
  rejectLabel?: string
  /** Semantic severity of the accept button. */
  acceptSeverity?: 'success' | 'info' | 'warn' | 'danger' | 'secondary'
}

export const useConfirm = (options: ConfirmDialogOptions): Promise<boolean> => {
  const confirm = usePrimeConfirm()
  return new Promise<boolean>((resolve) => {
    confirm.require({
      message: options.message,
      header: options.header ?? 'Confirm',
      icon: options.icon ?? 'pi pi-exclamation-triangle',
      acceptProps: {
        label: options.acceptLabel ?? 'Confirm',
        severity: options.acceptSeverity ?? 'danger',
      },
      rejectProps: {
        label: options.rejectLabel ?? 'Cancel',
        severity: 'secondary',
      },
      accept: () => resolve(true),
      reject: () => resolve(false),
    })
  })
}

/**
 * Setup-time confirm hook. PrimeVue's `useConfirm()` resolves the service via
 * `inject`, which only works during `setup()` — so calling {@link useConfirm}
 * from inside an async handler (or a detached mount like a drawer body) throws
 * "No PrimeVue Confirmation provided!". Call THIS once at setup and invoke the
 * returned promise fn from any handler. Mirrors the working pattern in
 * `views/users/UserListView.vue`.
 */
export const useConfirmDialog = (): ((options: ConfirmDialogOptions) => Promise<boolean>) => {
  const confirm = usePrimeConfirm()

  return (options: ConfirmDialogOptions): Promise<boolean> =>
    new Promise<boolean>((resolve) => {
      confirm.require({
        message: options.message,
        header: options.header ?? 'Confirm',
        icon: options.icon ?? 'pi pi-exclamation-triangle',
        acceptProps: {
          label: options.acceptLabel ?? 'Confirm',
          severity: options.acceptSeverity ?? 'danger',
        },
        rejectProps: {
          label: options.rejectLabel ?? 'Cancel',
          severity: 'secondary',
        },
        accept: () => resolve(true),
        reject: () => resolve(false),
      })
    })
}
